import { useState, type FormEvent } from 'react'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { BadgeCheck, CircleAlert, Download, Search, Wallet } from 'lucide-react'
import { PAYMENT_STATE_LABELS, formatAmount, type PaymentState } from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  Empty,
  Input,
  PageHeader,
  Pagination,
  SegmentedPills,
  Select,
  Stat,
  StatGrid,
  StatusBadge,
  Table,
  useAuth,
  useCan,
  useToast,
  type Column,
} from '@concours/ui'
import { errorMessage } from '../../lib/errors'
import { when, type Competition } from '../../lib/competition-types'
import type { Paginated } from '../../lib/rbac-types'
import { ConfirmPaymentModal } from './PaymentPage'
import {
  PAYMENTS_KEY,
  type AdminPaymentRow,
  type MethodsResponse,
  type StatusCounts,
} from '../../lib/payments'

const FILTERS: (PaymentState | 'all')[] = [
  'confirmed',
  'processing',
  'failed',
  'cancelled',
  'refunded',
  'all',
]

/**
 * Transactions (TODO phase 11) : filtres, anomalies, export pour le contrôle
 * financier. Toujours limitées aux concours de la portée de l'agent.
 */
/** Paiements qu'un agent peut valider après vérification chez le prestataire. */
const VALIDATABLE: PaymentState[] = ['processing', 'failed', 'cancelled']

export function PaymentsPage() {
  const canConfirm = useCan('payment.manual_entry')
  const [confirming, setConfirming] = useState<AdminPaymentRow | null>(null)
  const { session } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const [status, setStatus] = useState<PaymentState | 'all'>('all')
  const [competition, setCompetition] = useState('')
  const [method, setMethod] = useState('')
  const [anomaly, setAnomaly] = useState(false)
  const [search, setSearch] = useState('')
  const [draft, setDraft] = useState('')
  const [page, setPage] = useState(1)

  const competitions = useQuery({
    queryKey: ['admin', 'competitions', 'options'],
    queryFn: () =>
      session.api
        .get<Paginated<Competition>>('/admin/competitions', { query: { perPage: 100 } })
        .then((r) => r.data),
    staleTime: 60_000,
  })
  const methods = useQuery({
    queryKey: ['admin', 'payment-methods', 'options'],
    queryFn: () => session.api.get<MethodsResponse>('/admin/payment-methods').then((r) => r.data),
    staleTime: 60_000,
    retry: false,
  })
  const query = {
    status: status === 'all' ? undefined : status,
    competition: competition || undefined,
    method: method || undefined,
    anomaly: anomaly || undefined,
    search: search || undefined,
  }
  const list = useQuery({
    queryKey: [...PAYMENTS_KEY, { ...query, page }],
    queryFn: () =>
      session.api.get<Paginated<AdminPaymentRow> & { meta: { byStatus: StatusCounts } }>(
        '/admin/payments',
        { query: { ...query, page } }
      ),
    placeholderData: keepPreviousData,
  })
  const counts = list.data?.meta.byStatus ?? {}
  const total = Object.values(counts).reduce((a, b) => a + (b?.count ?? 0), 0)

  const exportCsv = useMutation({
    mutationFn: () => session.api.download('/admin/payments/export', { query }),
    onSuccess: (file) => {
      const url = URL.createObjectURL(file.blob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.filename ?? 'transactions.csv'
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  const columns: Column<AdminPaymentRow>[] = [
    {
      key: 'reference',
      header: 'Référence',
      cell: (r) => <span className="mono">{r.reference}</span>,
    },
    {
      key: 'candidate',
      header: 'Candidat',
      cell: (r) => (
        <span>
          {r.candidate}
          <br />
          <span className="text-muted mono" style={{ fontSize: 'var(--text-xs)' }}>
            {r.applicationNumber}
          </span>
        </span>
      ),
    },
    { key: 'method', header: 'Moyen', cell: (r) => r.methodLabel },
    {
      key: 'total',
      header: 'Montant',
      numeric: true,
      cell: (r) => formatAmount(r.total, r.currency),
    },
    { key: 'date', header: 'Date', cell: (r) => when(r.paidAt ?? r.createdAt) },
    {
      key: 'status',
      header: 'Statut',
      cell: (r) => (
        <span className="row" style={{ gap: 'var(--space-1)', flexWrap: 'wrap' }}>
          <StatusBadge domain="payment" status={r.status} />
          {r.anomaly && (
            <Badge variant="danger" dot>
              Anomalie
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'end',
      cell: (r) =>
        canConfirm &&
        VALIDATABLE.includes(r.status) && (
          <Button
            size="sm"
            variant="secondary"
            icon={<BadgeCheck />}
            onClick={(e) => {
              e.stopPropagation()
              setConfirming(r)
            }}
          >
            Valider
          </Button>
        ),
    },
  ]

  const submitSearch = (e: FormEvent) => {
    e.preventDefault()
    setSearch(draft.trim())
    setPage(1)
  }

  return (
    <>
      <PageHeader
        eyebrow="Dossiers"
        title="Paiements"
        description="Transactions des candidats, notifications des prestataires et anomalies. Un paiement n’est confirmé que si la référence, le montant et la devise correspondent exactement."
        actions={
          <Can perm="payment.export">
            <Button
              variant="secondary"
              icon={<Download />}
              loading={exportCsv.isPending}
              onClick={() => exportCsv.mutate()}
            >
              Exporter (CSV)
            </Button>
          </Can>
        }
      />
      <div className="stack" style={{ gap: 'var(--space-5)' }}>
        <StatGrid>
          <Stat
            label="Encaissé"
            value={formatAmount(counts.confirmed?.amount ?? 0)}
            hint={`${counts.confirmed?.count ?? 0} paiement(s) confirmé(s)`}
          />
          <Stat
            label="En cours"
            value={String(counts.processing?.count ?? 0)}
            hint="En attente du prestataire"
          />
          <Stat
            label="Échecs et annulations"
            value={String((counts.failed?.count ?? 0) + (counts.cancelled?.count ?? 0))}
          />
          <Stat
            label="Remboursé"
            value={formatAmount(counts.refunded?.amount ?? 0)}
            hint={`${counts.refunded?.count ?? 0} remboursement(s)`}
          />
        </StatGrid>
        <Card>
          <div className="filter-row" style={{ flexWrap: 'wrap', gap: 'var(--space-3)' }}>
            <SegmentedPills
              label="Statut"
              value={status}
              onChange={(v) => {
                setStatus(v)
                setPage(1)
              }}
              options={FILTERS.map((s) => ({
                value: s,
                label:
                  s === 'all'
                    ? `Tous · ${total}`
                    : `${PAYMENT_STATE_LABELS[s]} · ${counts[s]?.count ?? 0}`,
              }))}
            />
            <label className="check-row">
              <input
                type="checkbox"
                checked={anomaly}
                onChange={(e) => {
                  setAnomaly(e.target.checked)
                  setPage(1)
                }}
              />
              <span>
                <CircleAlert aria-hidden="true" size={14} /> Anomalies seulement
              </span>
            </label>
          </div>
          <div className="filter-row" style={{ flexWrap: 'wrap', gap: 'var(--space-3)' }}>
            <Select
              label="Concours"
              value={competition}
              onChange={(e) => {
                setCompetition(e.target.value)
                setPage(1)
              }}
              options={[
                { value: '', label: 'Tous les concours de ma portée' },
                ...(competitions.data ?? []).map((c) => ({
                  value: c.id,
                  label: `${c.reference} — ${c.title}`,
                })),
              ]}
            />
            <Select
              label="Moyen"
              value={method}
              onChange={(e) => {
                setMethod(e.target.value)
                setPage(1)
              }}
              options={[
                { value: '', label: 'Tous' },
                ...(methods.data ?? []).map((m) => ({ value: m.id, label: m.label })),
              ]}
            />
            <form
              role="search"
              className="row"
              style={{ gap: 'var(--space-2)', alignItems: 'end' }}
              onSubmit={submitSearch}
            >
              <Input
                label="Rechercher"
                placeholder="Référence, transaction, matricule, nom"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <Button type="submit" variant="secondary" icon={<Search />}>
                Rechercher
              </Button>
            </form>
          </div>
          {list.isError && <Alert tone="danger">{errorMessage(list.error)}</Alert>}
          <Table
            columns={columns}
            rows={list.data?.data ?? []}
            rowKey={(r) => r.id}
            loading={list.isPending}
            caption="Paiements"
            onRowClick={(r) => navigate(`/paiements/${r.id}`)}
            empty={<Empty icon={<Wallet aria-hidden="true" />} title="Aucune transaction" />}
          />
          {list.data && list.data.meta.total > list.data.meta.perPage && (
            <Pagination
              page={page}
              perPage={list.data.meta.perPage}
              total={list.data.meta.total}
              onPageChange={setPage}
            />
          )}
        </Card>
      </div>
      {confirming && (
        <ConfirmPaymentModal detail={confirming} onClose={() => setConfirming(null)} />
      )}
    </>
  )
}
